import React, { useMemo, useState } from "react";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "employment", label: "Employment" },
  { key: "documents", label: "Documents" },
  { key: "roles", label: "Roles" },
  { key: "notes", label: "Notes" },
];

function TabButton({ active, children, onClick }) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-4 py-2 rounded-xl text-sm font-medium transition border",
        active
          ? "bg-black text-white border-black"
          : "bg-white dark:bg-neutral-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function SectionCard({ title, action, children }) {
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-700 shadow-sm p-5">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 className="text-lg font-semibold text-black dark:text-white">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function HRProfileTabs({
  person,
  employment = [],
  documents = [],
  roles = [],
  notes = [],
  newNote,
  setNewNote,
  savingNote,
  onCreateNote,
  onOpenEmploymentModal,
  onOpenDocumentModal,
  onOpenRoleModal,
  onOpenCloseEmployment,
  activeEmployment,
  onDeleteRole,
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [deletingRoleId, setDeletingRoleId] = useState(null);
  

  const stats = useMemo(
    () => ({
      employmentCount: employment.length,
      documentsCount: documents.length,
      rolesCount: roles.length,
      notesCount: notes.length,
    }),
    [employment, documents, roles, notes]
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {TABS.map((tab) => (
          <TabButton
            key={tab.key}
            active={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </TabButton>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <SectionCard title="Profile summary">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="border border-gray-200 dark:border-neutral-700 rounded-xl p-3">
                <div className="text-gray-500">Full name</div>
                <div className="font-medium">{person?.full_name || `${person?.first_name || ""} ${person?.last_name || ""}`.trim() || "-"}</div>
              </div>
              <div className="border border-gray-200 dark:border-neutral-700 rounded-xl p-3">
                <div className="text-gray-500">Person type</div>
                <div className="font-medium">{person?.person_type || "-"}</div>
              </div>
              <div className="border border-gray-200 dark:border-neutral-700 rounded-xl p-3">
                <div className="text-gray-500">Department</div>
                <div className="font-medium">{activeEmployment?.department_name || person?.department_name || "-"}</div>
              </div>
              <div className="border border-gray-200 dark:border-neutral-700 rounded-xl p-3">
                <div className="text-gray-500">Team</div>
                <div className="font-medium">{activeEmployment?.team_name || person?.team_name || "-"}</div>
              </div>
              <div className="border border-gray-200 dark:border-neutral-700 rounded-xl p-3">
                <div className="text-gray-500">Position</div>
                <div className="font-medium">{activeEmployment?.position_title || person?.position_title || "-"}</div>
              </div>
              <div className="border border-gray-200 dark:border-neutral-700 rounded-xl p-3">
                <div className="text-gray-500">Manager</div>
                <div className="font-medium">{activeEmployment?.manager_name || person?.manager_name || "-"}</div>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Quick stats">
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-gray-200 dark:border-neutral-700 rounded-xl p-4">
                <div className="text-sm text-gray-500">Employment records</div>
                <div className="text-2xl font-semibold mt-1">{stats.employmentCount}</div>
              </div>
              <div className="border border-gray-200 dark:border-neutral-700 rounded-xl p-4">
                <div className="text-sm text-gray-500">Documents</div>
                <div className="text-2xl font-semibold mt-1">{stats.documentsCount}</div>
              </div>
              <div className="border border-gray-200 dark:border-neutral-700 rounded-xl p-4">
                <div className="text-sm text-gray-500">Roles</div>
                <div className="text-2xl font-semibold mt-1">{stats.rolesCount}</div>
              </div>
              <div className="border border-gray-200 dark:border-neutral-700 rounded-xl p-4">
                <div className="text-sm text-gray-500">Notes</div>
                <div className="text-2xl font-semibold mt-1">{stats.notesCount}</div>
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {activeTab === "employment" && (
        <SectionCard
          title="Employment history"
          action={
            <div className="flex gap-2">
              <button
                onClick={onOpenEmploymentModal}
                className="px-3 py-2 rounded-xl bg-black text-white text-sm"
              >
                Add employment
              </button>
              {activeEmployment && Number(activeEmployment.active_flag) === 1 && (
                <button
                  onClick={() => onOpenCloseEmployment?.(activeEmployment)}
                  className="px-3 py-2 rounded-xl border border-red-300 text-red-700 bg-red-50 text-sm"
                >
                  Close active
                </button>
              )}
            </div>
          }
        >
          <div className="space-y-3">
            {employment.length === 0 ? (
              <div className="text-sm text-gray-500">No employment records.</div>
            ) : (
              employment.map((row) => (
                <div
                  key={row.id}
                  className="border border-gray-200 dark:border-neutral-700 rounded-xl p-4"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="font-medium">
                      {row.position_title || "-"} · {row.employment_type || "-"}
                    </div>
                    <div className="text-sm text-gray-500">
                      {row.start_date || "-"} → {row.end_date || "active"}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {row.department_name || "-"} / {row.team_name || "-"} / {row.manager_name || "-"}
                  </div>
                  <div className="text-sm mt-2">
                    Status: {row.employment_status || "-"} · Work mode: {row.work_mode || "-"} · Capacity: {row.work_capacity_percent || 0}%
                  </div>
                </div>
              ))
            )}
          </div>
        </SectionCard>
      )}

      {activeTab === "documents" && (
        <SectionCard
          title="Documents"
          action={
            <button
              onClick={onOpenDocumentModal}
              className="px-3 py-2 rounded-xl bg-black text-white text-sm"
            >
              Add document
            </button>
          }
        >
          <div className="space-y-3">
            {documents.length === 0 ? (
              <div className="text-sm text-gray-500">No documents.</div>
            ) : (
              documents.map((doc) => (
                <div
                  key={doc.id}
                  className="border border-gray-200 dark:border-neutral-700 rounded-xl p-4"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="font-medium">{doc.title || "-"}</div>
                    <div className="text-sm text-gray-500">
                      {doc.document_type || "-"}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Issue: {doc.issue_date || "-"} · Expiry: {doc.expiry_date || "-"}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Visibility: {doc.visibility_scope || "-"}
                  </div>
                  {doc.file_url ? (
                    <a
                      href={doc.file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-2 text-sm underline"
                    >
                      Open document
                    </a>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </SectionCard>
      )}

      {activeTab === "roles" && (
        <SectionCard
          title="Roles"
          action={
            <button
              onClick={onOpenRoleModal}
              className="px-3 py-2 rounded-xl bg-black text-white text-sm"
            >
              Add role
            </button>
          }
        >
          <div className="space-y-3">
            {roles.length === 0 ? (
              <div className="text-sm text-gray-500">No roles assigned.</div>
            ) : (
              roles.map((role) => (
                <div
                  key={role.id}
                  className="border border-gray-200 dark:border-neutral-700 rounded-xl p-4"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
  <div className="font-medium">{role.role_name || "-"}</div>

  <div className="flex items-center gap-2">
    <div className="text-sm text-gray-500">
      {role.role_scope || "-"}
    </div>

    <button
      disabled={deletingRoleId === role.id}
      onClick={async () => {
        if (!window.confirm("Delete this role?")) return;

        try {
          setDeletingRoleId(role.id);
          await onDeleteRole?.(role.id);
        } finally {
          setDeletingRoleId(null);
        }
      }}
      className="px-2 py-1 rounded-lg border border-red-300 text-red-700 bg-red-50 text-xs hover:bg-red-100 disabled:opacity-50"
    >
      {deletingRoleId === role.id ? "Deleting..." : "Delete"}
    </button>
  </div>
</div>
                  <div className="text-sm text-gray-500 mt-1">
                    Key: {role.role_key || "-"} · Active: {Number(role.active_flag) === 1 ? "Yes" : "No"}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Valid: {role.valid_from || "-"} → {role.valid_to || "-"}
                  </div>
                </div>
              ))
            )}
          </div>
        </SectionCard>
      )}

      {activeTab === "notes" && (
        <SectionCard title="Notes">
          <form onSubmit={onCreateNote} className="space-y-3 mb-4">
            <textarea
              className="w-full min-h-[120px] border border-gray-300 dark:border-neutral-700 rounded-xl px-3 py-2 bg-white dark:bg-neutral-950"
              placeholder="Write a HR note..."
              value={newNote}
              onChange={(e) => setNewNote?.(e.target.value)}
            />
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={savingNote}
                className="px-4 py-2 rounded-xl bg-black text-white disabled:opacity-60"
              >
                {savingNote ? "Saving..." : "Add note"}
              </button>
            </div>
          </form>

          <div className="space-y-3">
            {notes.length === 0 ? (
              <div className="text-sm text-gray-500">No notes.</div>
            ) : (
              notes.map((note) => (
                <div
                  key={note.id}
                  className="border border-gray-200 dark:border-neutral-700 rounded-xl p-4"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="font-medium">{note.note_type || "general"}</div>
                    <div className="text-sm text-gray-500">{note.created_at || "-"}</div>
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300 mt-2 whitespace-pre-wrap">
                    {note.note_text}
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    Visibility: {note.visibility || "-"}
                  </div>
                </div>
              ))
            )}
          </div>
        </SectionCard>
      )}
    </div>
  );
}